import { useSyncExternalStore } from 'react'
import { api } from './api'

/* ================================================================== */
/*  Auth session, shared with the myNext50 webapp.                     */
/*                                                                     */
/*  Login / signup / onboarding all live in the webapp, not here. This  */
/*  marketing site never collects credentials; it only:                 */
/*    1. reads the shared session via GET /auth/me (httpOnly cookie),   */
/*    2. hands gated CTAs off to the webapp's /login or /register.       */
/*                                                                     */
/*  The session cookie is set by the API on *.lifecraft4u.com. If this   */
/*  site is deployed same-site (a lifecraft4u.com subdomain) the browser */
/*  sends that cookie and /auth/me returns the user, so we can show      */
/*  logged-in state. If it's on a different domain the cookie can't be   */
/*  shared, /auth/me simply 401s, everyone is treated as a guest, and    */
/*  every CTA routes to the webapp to sign in. Either way the site works.*/
/* ================================================================== */

/** The webapp that owns auth. Defaults to https://web.lifecraft4u.com;
 *  override with VITE_WEBAPP_URL per environment. */
export const WEBAPP_URL = (import.meta.env.VITE_WEBAPP_URL ?? 'https://web.lifecraft4u.com').replace(/\/$/, '')

export type User = {
  id: string
  name: string
  email: string
  role: string
  display_name?: string
  alias?: string | null
  phoneNumber?: number
  isEmailVerified?: boolean
}

/** Best name to greet the user by. */
export function displayName(user: User | null): string {
  return user?.display_name?.trim() || user?.name?.trim() || 'Member'
}

/** First name only, for tight spaces like the nav chip. */
export function firstName(user: User | null): string {
  return displayName(user).split(/\s+/)[0]
}

/** 1-2 letter initials for the avatar fallback. */
export function initials(user: User | null): string {
  const parts = displayName(user).split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || 'M'
}

type Status = 'loading' | 'authed' | 'guest'
type AuthState = { status: Status; user: User | null }

let state: AuthState = { status: 'loading', user: null }
const listeners = new Set<() => void>()

function set(next: AuthState) {
  state = next
  for (const cb of listeners) cb()
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

function getSnapshot() {
  return state
}

/**
 * Restore the session from the httpOnly cookie via GET /auth/me. Safe to call
 * repeatedly; only the first call hits the network. Invoke once on app start.
 */
let hydrating: Promise<void> | null = null
export function hydrateAuth(): Promise<void> {
  if (hydrating) return hydrating
  hydrating = (async () => {
    try {
      const user = await api<User>('/auth/me')
      set({ status: 'authed', user })
    } catch {
      set({ status: 'guest', user: null })
    }
  })()
  return hydrating
}

/** Sign out: clear the server cookie (best-effort) and drop local state. */
export async function logout(): Promise<void> {
  try {
    await api('/auth/logout')
  } catch {
    // Ignore: even if the server call fails, forget the session locally.
  }
  set({ status: 'guest', user: null })
}

export function useAuth() {
  const s = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return {
    user: s.user,
    authed: s.status === 'authed',
    loading: s.status === 'loading',
    status: s.status,
    logout,
  }
}

/* ------------------------------------------------------------------ */
/*  Handoff URLs into the webapp                                       */
/* ------------------------------------------------------------------ */

/** Where to send the user back to after they finish in the webapp. Never the
 *  auth routes themselves, otherwise a freshly-logged-in user bounces straight
 *  back into the handoff. Falls back to home. */
function returnTo(): string {
  if (typeof window === 'undefined') return '/'
  const { origin, pathname } = window.location
  const isAuthPath = pathname === '/login' || pathname === '/signup'
  return origin + (isAuthPath ? '/' : pathname)
}

function webappUrl(path: 'login' | 'register', extra?: Record<string, string>): string {
  const url = new URL(`${WEBAPP_URL}/${path}`)
  url.searchParams.set('redirect', returnTo())
  if (extra) for (const [k, v] of Object.entries(extra)) url.searchParams.set(k, v)
  return url.toString()
}

/** Webapp login URL, with a redirect back to the current page. */
export function loginUrl(): string {
  return webappUrl('login')
}

/** Webapp register URL. Pass a role (e.g. 'lifecrafter') to open that flow. */
export function registerUrl(role?: string): string {
  return webappUrl('register', role ? { role } : undefined)
}

/* Webapp community ids (from the public categories feed) for the live-wired
   communities, so we can deep-link a logged-in member straight to the right
   tab of the webapp community page. */
const WEBAPP_COMMUNITY_IDS: Record<string, string> = {
  Cosmos: '695970736471de61464dfb60',
  Rhythm: '6959694b6471de61464dfb5c',
  Zen: '695970966471de61464dfb65',
}
export type WebappTab = 'events' | 'experts' | 'community_circles'

/** Webapp deep-link to a community tab, or null if that community isn't on the
 *  webapp yet (caller falls back to the in-page anchor). Tabs map to the webapp:
 *  events → Events, experts → Lifecrafter, community_circles → Community Circles. */
export function communityWebappUrl(community: string, tab: WebappTab): string | null {
  const id = WEBAPP_COMMUNITY_IDS[community.trim()]
  return id ? `${WEBAPP_URL}/my-communities/${id}?tab=${tab}` : null
}

/** Webapp community home page (the "Join the community" destination), or null. */
export function communityHomeUrl(community: string): string | null {
  const id = WEBAPP_COMMUNITY_IDS[community.trim()]
  return id ? `${WEBAPP_URL}/my-communities/${id}` : null
}

/** Webapp profile/booking page for a single LifeCrafter (by their backend id). */
export function lifecrafterWebappUrl(id: string): string {
  return `${WEBAPP_URL}/profile/${id}`
}

/** Webapp event page (the "Book Now" registration page) for a single event. */
export function eventWebappUrl(id: string): string {
  return `${WEBAPP_URL}/events/${id}`
}

/** Webapp 1-on-1 booking page. With just a community it lands on that community's
 *  Book 1:1 programs list; with a lifecrafter id it opens that specific crafter's
 *  booking. Returns null if the community isn't wired to the webapp yet. */
export function consultWebappUrl(community: string, lifecrafterId?: string): string | null {
  const categoryId = WEBAPP_COMMUNITY_IDS[community.trim()]
  if (!categoryId) return null
  const q = new URLSearchParams({ categoryId })
  if (lifecrafterId) q.set('lifecrafterId', lifecrafterId)
  return `${WEBAPP_URL}/one-to-one-session?${q.toString()}`
}

/**
 * Route target for a gated CTA: the real in-site target when authed, else the
 * `fallback` auth route (a local /login or /signup redirector that bounces to
 * the webapp). Keeps existing call sites working unchanged.
 */
export function gatedTarget(authed: boolean, target: string, fallback = '/login') {
  return authed ? target : fallback
}

/**
 * LifeCrafter (host) signup entry. Role-tagged so the /signup redirector opens
 * the webapp's LifeCrafter onboarding rather than the member one.
 */
export const LIFECRAFTER_AUTH = '/signup?role=lifecrafter'
